// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { ReactNode } from "react"
import ReactMarkdown, { type Components } from "react-markdown"
import remarkGfm from "remark-gfm"

type Kids = { children?: ReactNode }

/**
 * Renders Amy's replies as markdown (owner-reported 2026-08-24: raw `**`
 * and `|` table pipes were showing as literal text). GFM enabled for the
 * tables Amy produces for plans/nutrition. react-markdown never injects
 * raw HTML, so model output stays inert; links open in a new tab with
 * `rel="noopener noreferrer"`. Streaming-safe: the partial markdown is
 * simply re-parsed on every chunk.
 */
const components: Components = {
  p: ({ children }: Kids) => <p className="my-1 leading-relaxed">{children}</p>,
  strong: ({ children }: Kids) => <strong className="font-semibold text-ink">{children}</strong>,
  ul: ({ children }: Kids) => <ul className="my-1 list-disc space-y-0.5 pl-5">{children}</ul>,
  ol: ({ children }: Kids) => <ol className="my-1 list-decimal space-y-0.5 pl-5">{children}</ol>,
  li: ({ children }: Kids) => <li className="leading-relaxed">{children}</li>,
  h1: ({ children }: Kids) => <p className="mt-2 mb-1 text-sm font-bold text-ink">{children}</p>,
  h2: ({ children }: Kids) => <p className="mt-2 mb-1 text-sm font-bold text-ink">{children}</p>,
  h3: ({ children }: Kids) => (
    <p className="mt-2 mb-1 text-sm font-semibold text-ink">{children}</p>
  ),
  h4: ({ children }: Kids) => (
    <p className="mt-2 mb-1 text-sm font-semibold text-ink">{children}</p>
  ),
  a: ({ href, children }: Kids & { href?: string }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline decoration-green underline-offset-2"
    >
      {children}
    </a>
  ),
  code: ({ children, className }: Kids & { className?: string }) =>
    className ? (
      <code className="block overflow-x-auto rounded-bb-md bg-ink/5 px-2 py-1 font-mono text-xs">
        {children}
      </code>
    ) : (
      <code className="rounded bg-ink/5 px-1 font-mono text-xs">{children}</code>
    ),
  pre: ({ children }: Kids) => <pre className="my-1">{children}</pre>,
  blockquote: ({ children }: Kids) => (
    <blockquote className="my-1 border-l-2 border-green/50 pl-3 text-ink-2">{children}</blockquote>
  ),
  hr: () => <hr className="my-2 border-line" />,
  table: ({ children }: Kids) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }: Kids) => <thead className="bg-ink/5">{children}</thead>,
  th: ({ children }: Kids) => (
    <th className="border border-line px-2 py-1 text-left font-semibold text-ink">{children}</th>
  ),
  td: ({ children }: Kids) => (
    <td className="border border-line px-2 py-1 align-top">{children}</td>
  ),
}

export function AmyMarkdown({ content }: { content: string }) {
  return (
    <div className="text-sm text-ink [overflow-wrap:anywhere]">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  )
}
